import React, { useState, useEffect, useRef } from 'react'
import Button from 'components/button'
import { IContextMenuState, OptionProps } from 'types'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { ChevronDown, Cross } from 'components/icons'
import SelectMenu from 'components/select/components/menu'
import Spinner from 'components/spinner'

export interface SelectProps {
  /** 'sm' is 32px tall, as shadcn/ui's small select is: on a panel's 32px rhythm, with its rows and pills. */
  size?: 'default' | 'sm'
  options: Array<OptionProps>
  onOptionClick: (option: string) => void
  loading?: boolean
  disabled?: boolean
  multiselect?: boolean
  optionsSelected?: string[]
  groupBy?: boolean
  setOptionsSelected?: (selectedOptions: string[]) => void
  error?: string
  info?: string
  customAddText?: string
  customRemoveText?: string
  switchColours?: boolean
  defaultOpen?: boolean
  placeholder?: string
  label?: string
  noOptionsMessage?: React.ReactNode
  inputId?: string
  clearable?: boolean
  searchable?: boolean
  className?: string
  menuWidth?: number
  onCreateOption?: (value: string) => void
  // When set, and two or more options can be added, an "Add all" row at the foot of the list passes every option it
  // shows (only those matching the search, once there is one) that isn't disabled or already selected, then closes it.
  onAddAll?: (values: string[]) => void
  // The search box's prompt, when the list takes more than a search (a value of one's own, say).
  searchPlaceholder?: string
}

const Select: React.FC<SelectProps> = ({
  options,
  onOptionClick,
  disabled,
  loading,
  multiselect,
  optionsSelected,
  groupBy,
  setOptionsSelected,
  error,
  info,
  customAddText,
  customRemoveText,
  switchColours,
  defaultOpen,
  placeholder,
  label,
  noOptionsMessage: noOptionsMessageProp,
  inputId,
  clearable,
  searchable = true,
  className,
  size = 'default',
  menuWidth,
  onCreateOption,
  onAddAll,
  searchPlaceholder
}) => {
  const noOptionsMessage = noOptionsMessageProp ?? <span>No options found</span>
  const [selectMenuOpen, setSelectMenuOpen] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [width, setWidth] = useState<number>(200)

  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (defaultOpen && buttonRef.current) {
      buttonRef.current.click()
    }
  }, [])

  // The list going away with the keyboard in it (an option picked with Enter, Esc) gives the keyboard back to the
  // field, rather than leaving it on the page. A press elsewhere still moves it to wherever was pressed.
  const setListState: React.Dispatch<React.SetStateAction<IContextMenuState>> = (next) => {
    const resolved = typeof next === 'function' ? next(selectMenuOpen) : next
    if (selectMenuOpen.open && !resolved.open && document.activeElement?.closest('#select-options')) {
      buttonRef.current?.focus()
    }
    setSelectMenuOpen(resolved)
  }

  const div = (node: HTMLDivElement | null) => {
    if (node !== null) {
      setWidth(node.getBoundingClientRect().width)
    }
  }

  // Nothing chosen: no value, or only empty ones that aren't themselves an option (a "None" option
  // whose value is '' is a choice, and shows as itself rather than with the placeholder beside it).
  const isNoOption =
    (!Array.isArray(optionsSelected) ||
      optionsSelected.filter((key) => key || options.some((option) => option.value === key)).length === 0) &&
    !loading

  return (
    <div className="h-full" ref={div} id="select-component" data-testid="select-menu">
      {label && <div className="text-muted text-sm mb-[5px]">{label}</div>}
      <Button
        id={inputId}
        innerRef={buttonRef}
        // In the manner of shadcn/ui's combobox, matching the text field: 36px from its padding
        // (or its container's height, where that's set), 12px in, 8px corners, a light border, and
        // the text field's blue border and ring while focused or open.
        className={`h-full w-full flex items-center gap-2 ${size === 'sm' ? 'py-[5px] rounded-md' : 'py-[7px] rounded-lg'} px-3 border-divider bg-field hover:bg-field hover:border-faint text-[13px] leading-5 font-normal text-body focus-visible:outline-none focus-visible:border-blue focus-visible:ring-3 focus-visible:ring-blue/15 ${selectMenuOpen.open ? 'border-blue ring-3 ring-blue/15 hover:border-blue' : ''} ${className || ''}`}
        internalType="outline"
        aria-haspopup="listbox"
        aria-expanded={selectMenuOpen.open}
        disabled={disabled}
        onClick={(event) => {
          const rect = event.currentTarget.getBoundingClientRect()
          // Measured as it opens, so the list is as wide as the field, whatever the layout did since
          // the page loaded.
          setWidth(rect.width)
          setSelectMenuOpen({
            open: true,
            top: `${rect.bottom + 6}px`,
            left: `${rect.left}px`,
            right: 'auto',
            bottom: 'auto'
          })
        }}
      >
        {/* What's picked at the weight it has in the list, as the menus set their rows; a placeholder quieter. */}
        <div className="flex items-center overflow-hidden w-full h-full font-medium">
          {loading && (
            <div className="p-1 min-w-[100px]">
              <Spinner size="small" />
            </div>
          )}
          {Array.isArray(optionsSelected) &&
            optionsSelected.length > 0 &&
            !loading &&
            optionsSelected.map((key: string, index: number) => {
              const selectedOption = options.find((option) => option.value === key)
              if (selectedOption) {
                return (
                  <div
                    key={index}
                    title={selectedOption.label}
                    className={`${
                      multiselect
                        ? 'flex items-center justify-center rounded-md mr-1.5 truncate bg-surface-sunken h-5 px-1.5 text-[12px] min-w-fit'
                        : 'truncate'
                    } ${selectedOption.icon ? 'flex items-center gap-2' : ''}`}
                  >
                    {selectedOption.icon}
                    <span className={selectedOption.icon ? 'truncate' : undefined}>{selectedOption.label}</span>
                  </div>
                )
              }
            })}
          {isNoOption && <div className="truncate font-normal text-faint">{placeholder || 'None selected'}</div>}
        </div>
        <div className="ml-auto font-normal flex items-center gap-1 text-muted [&_svg]:size-4">
          {clearable && !isNoOption && (
            <div
              onClick={(e) => {
                e.stopPropagation()
                onOptionClick('')
              }}
              className="flex hover:text-body"
            >
              <Cross />
            </div>
          )}
          {/* One chevron, turned over while the list is open. */}
          <ChevronDown className={`transition-transform duration-150 ${selectMenuOpen.open ? 'rotate-180' : ''}`} />
        </div>
      </Button>
      {info && <div className="text-muted text-sm mt-[5px]">{info}</div>}
      {error && <div className="text-red text-sm mt-[5px]">{error}</div>}

      {selectMenuOpen.open && (
        <SelectMenu
          id="select-options"
          menuState={selectMenuOpen}
          multiselect={multiselect}
          setMenuState={setListState}
          loading={loading}
          options={options}
          optionsSelected={optionsSelected}
          onOptionClick={onOptionClick}
          groupBy={groupBy}
          width={Math.max(width, menuWidth ?? 0)}
          setOptionsSelected={setOptionsSelected}
          customAddText={customAddText}
          customRemoveText={customRemoveText}
          switchColours={switchColours}
          noOptionsMessage={noOptionsMessage}
          searchable={searchable}
          onCreateOption={onCreateOption}
          onAddAll={onAddAll}
          searchPlaceholder={searchPlaceholder}
        />
      )}
    </div>
  )
}

export default Select
